import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgxSpinnerModule } from 'ngx-spinner';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SharedModule } from '../../../shared/shared.module';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { DatePickerComponent } from '../../../shared/components/date-picker/date-picker.component';
import { DEtrTradeProvider } from '../../common/providers/d-etr-trade.provider';
import { numberValueFormatter } from '../../../shared/helpers/number-value-formatter';
import { OpenTradeBaseComponent } from '../open-trade-base.component';

@Component({
  standalone: true,
  selector: 'app-d-etr-trade',
  templateUrl: './d-etr-trade.component.html',
  styleUrls: ['./d-etr-trade.component.scss'],
  imports: [
    CommonModule,
    NgxSpinnerModule,
    ReactiveFormsModule,
    SharedModule,
    FormsModule,
    TooltipModule,
    DatePickerComponent,
  ],
  providers: [DEtrTradeProvider],
})
export class DEtrTradeComponent extends OpenTradeBaseComponent {
  readonly #dEtrTradeProvider = inject(DEtrTradeProvider);

  dEtrRSATradeForm: FormGroup;
  dEtrTradeForm: FormGroup;
  hasDebtor: string;

  protected override onInitialize(): void {
    this.hasDebtor = this.currentData?.debtorId;

    if (this.isEditTrade) {
      this.componentService.pageTitle.set('Edit a Trade');
      if (this.currentData.type === 'RSA') {
        this.detectExistRsa = false;
        this.isShowRsaForm = true;
        this.#dEtrTradeProvider.initRSAForm({
          ...this.currentData,
          debtorId: this.hasDebtor,
          type: 'RSA',
        });
        this.dEtrRSATradeForm = this.#dEtrTradeProvider.dEtrRSATradeForm;
      } else {
        this.detectExistRsa = false;
        this.isShowRsaForm = false;
        this.initdEtrTradeForm(
          {
            ...this.currentData,
            faceValue:
              this.currentData.leftFaceValue || this.currentData.faceValue,
            rsaTradeId: this.currentData.tradeId,
          },
          true,
        );
      }
    }
  }

  protected override getMainForm(): FormGroup {
    return this.isShowRsaForm ? this.dEtrRSATradeForm : this.dEtrTradeForm;
  }

  protected override onCreateSuccess(response: any, data: any): void {
    if (response.type === 'RSA') {
      this.initdEtrTradeForm(response);
    } else {
      this.closeModal();
    }
  }

  onSubmit(): void {
    if (this.dEtrRSATradeForm.invalid) return;
    const rawData = this.dEtrRSATradeForm.getRawValue();
    let formattedRawData = { ...rawData };
    formattedRawData.type = 'RSA';
    formattedRawData.debtor = rawData.debtor ? rawData.debtor : '';
    this.submitFormData(formattedRawData);
  }

  onSubmitETRTrade(): void {
    if (this.dEtrTradeForm.invalid) return;
    const rawData = this.dEtrTradeForm.getRawValue();
    let formattedRawData = { ...rawData };
    formattedRawData.type = this.type ? `${this.type}` : '';
    formattedRawData.edso = rawData.edso ? Number(rawData.edso) : null;
    formattedRawData.faceValue = rawData.faceValue
      ? Number(rawData.faceValue)
      : null;
    formattedRawData.deposit = rawData.deposit ? +rawData.deposit / 100 : null;
    delete formattedRawData.description;
    delete formattedRawData.pod;
    delete formattedRawData.rejectedMessage;

    this.spinnerService.setShowSpinner(true);
    this.currentData?.acquisitionId
      ? this.doUpdate(formattedRawData)
      : this.doCreate(formattedRawData);
  }

  selectCreditorForDetectRSA(event: any): void {
    if (!(event && event.id)) {
      this.detectExistRsa = true;
      return;
    }

    this.detectRSAForDebtor(
      event.id,
      (data) => {
        this.initdEtrTradeForm(data);
        this.dEtrTradeForm = this.#dEtrTradeProvider.dEtrTradeForm;
      },
      () => {
        this.#dEtrTradeProvider.initRSAForm({
          debtorId: event.id,
          type: 'RSA',
        });
        this.dEtrRSATradeForm = this.#dEtrTradeProvider.dEtrRSATradeForm;
      },
    );
  }

  override fileChange(element: Event, acronym: string): void {
    super.fileChange(element, acronym, this.dEtrTradeForm);
  }

  get form2TradeDocumentsControl() {
    return this.getTradeDocumentsControl(this.dEtrTradeForm);
  }

  private initdEtrTradeForm(data: any, isEdit?: boolean): void {
    this.dEtrRSATradeForm?.reset();
    this.isShowRsaForm = false;
    this.#dEtrTradeProvider.initTradeForm(data, isEdit);
    this.dEtrTradeForm = this.#dEtrTradeProvider.dEtrTradeForm;

    const selectedCredebtor =
      this.creditorList.find((creditor) => creditor.id === data.creditorId) ||
      this.debtorList.find((debtor) => debtor.id === data.debtorId);

    if (selectedCredebtor) {
      this.showTradeDocumentForEdit(
        selectedCredebtor,
        this.dEtrTradeForm,
        this.currentData?.documents,
      );
    }

    this.totalFaceValue = data.faceValue;
    this.remainingValue = numberValueFormatter({ value: data.faceValue });
    this.dEtrTradeForm.get('faceValue')?.valueChanges.subscribe((value) => {
      this.calculateRemainingValue(value);
    });

    this.subscribeToInvoiceDateChange(this.dEtrTradeForm);
    this.subscribeToPaymentTermsChange(this.dEtrTradeForm);
    this.subscribeToExpectedDateChange(this.dEtrTradeForm);
  }
}
